/**
 * Badge Component
 * Status indicator with color variants
 */

import { forwardRef } from 'react';
import type { BadgeProps } from '../types/components';
import { cn } from '../utils/cn';

const variantStyles: Record<string, string> = {
  default: 'bg-gray-50 text-gray-600 shadow-sm shadow-gray-900/5 dark:bg-slate-800 dark:text-slate-300',
  success: 'bg-gray-50 text-gray-700 shadow-sm shadow-gray-900/5 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-gray-50 text-gray-700 shadow-sm shadow-gray-900/5 dark:bg-yellow-900/30 dark:text-yellow-400',
  danger: 'bg-gray-50 text-gray-700 shadow-sm shadow-gray-900/5 dark:bg-red-900/30 dark:text-red-400',
  info: 'bg-gray-50 text-gray-700 shadow-sm shadow-gray-900/5 dark:bg-blue-900/30 dark:text-blue-400',
};

const dotColors: Record<string, string> = {
  default: 'bg-gray-500',
  success: 'bg-gray-700',
  warning: 'bg-yellow-500',
  danger: 'bg-gray-500',
  info: 'bg-gray-700',
};

const sizeStyles: Record<string, string> = {
  xs: 'px-1 py-0 text-[9px]',
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

/** Overlay style for photo card badges — frosted glass on dark background */
const overlayStyle = 'bg-black/50 text-white backdrop-blur-sm border-transparent';

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      dot = false,
      overlay = false,
      removable = false,
      onRemove,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center font-medium rounded-full',
          overlay ? overlayStyle : variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {dot && (
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full mr-1.5',
              dotColors[variant]
            )}
            aria-hidden="true"
          />
        )}
        {children}
        {removable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
            className={cn(
              'ml-1.5 -mr-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center',
              'hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-offset-1',
              'focus:ring-current'
            )}
            aria-label="Remove"
          >
            <svg
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

/**
 * Helper function to map severity to badge variant
 */
export function severityToBadgeVariant(
  severity: 'critical' | 'high' | 'medium' | 'low'
): 'danger' | 'warning' | 'info' | 'success' {
  const mapping = {
    critical: 'danger' as const,
    high: 'warning' as const,
    medium: 'info' as const,
    low: 'success' as const,
  };
  return mapping[severity];
}

export default Badge;
